// frontend/src/lib/format.ts
// Small display formatters shared by the staff record pages (authors, publishers).

export const yearOf = (iso?: string | null) => {
  if (!iso) return null;
  const y = String(iso).slice(0, 4);
  return /^\d{4}$/.test(y) ? y : null;
};

/** "14 Mar 2024", or an em dash when there is no date. */
export const formatDate = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

/** "1 book", "12 books". */
export const plural = (n: number, one: string, many: string) =>
  `${n.toLocaleString()} ${n === 1 ? one : many}`;

/** Years as a span: "2005", "2005–2014", or null when none are known. */
export const yearSpan = (years: (number | null)[] | undefined) => {
  const known = (years ?? []).filter((y): y is number => typeof y === 'number');
  if (known.length === 0) return null;
  const lo = Math.min(...known);
  const hi = Math.max(...known);
  return lo === hi ? String(lo) : `${lo}–${hi}`;
};

/** "panmacmillan.com" from "https://www.panmacmillan.com/about"; the input if it isn't a URL. */
export const hostOf = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

/** "79% of total" for a KPI caption; a non-breaking space holds the line while loading. */
export const shareOfTotal = (part?: number, total?: number) =>
  part !== undefined && total ? `${Math.round((part / total) * 100)}% of total` : '\u00a0';

/** 0–1 for a KPI meter; 0 while loading so the bar is drawn empty, not missing. */
export const ratio = (part?: number, total?: number) =>
  part !== undefined && total ? part / total : 0;

/** "PM" for "Pan Macmillan", "AG" for "Agriculture"; skips "The" and symbols like "&". */
export const initialsOf = (name: string) => {
  const words = name.trim().split(/\s+/).filter((w) => /^[\p{L}\p{N}]/u.test(w) && !/^the$/i.test(w));
  if (words.length === 0) return '—';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

/** Today as YYYY-MM-DD in the librarian's own time zone (not UTC, which is still
 *  "yesterday" in the early hours east of Greenwich). For date inputs and defaults. */
export const localToday = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
};
